import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { VirtuosoGrid } from 'react-virtuoso';
// Helpers
import {
  ATHLETES_BASE_PATH,
  ATHLETES_PAGE_DESCRIPTION,
  ATHLETES_PER_PAGE,
  buildAthletesCanonicalLink,
  buildAthletesPageTitle,
  buildAthletesPaginatedHref,
  clampAthletesPage,
  getAthletesPageCount,
  getAthletesPageFromQuery,
  getAthletesPaginationQuery,
} from 'helpers/athletes-page';
import { getEnvLink } from 'helpers/routes';
import { generateItemListStructuredData } from 'helpers/structured-data';
// Context
import { useFiltersContext } from 'components/common3/PageWithFiltersWrapper/PageWithFiltersContext';
// Api
import { GET_STORES } from 'api/store/queries';
// Types
import { UserRole } from 'api/graphql-global-types';
import {
  GetStores,
  GetStoresVariables,
  GetStores_stores_entities,
} from 'api/store/types/GetStores';
import { SearchLocation } from 'api/graphql-analytics-global-types';
// Layouts
import Head from 'layouts/Head/Head';
// Components
import InfiniteScrollLoader from 'ui3/InfiniteScrollLoader/InfiniteScrollLoader';
import StoreListItem from 'components/common3/StoresList/components/StoreListItem/StoreListItem';
import NotFound from 'components/common/NotFound/NotFound';
import PageWithFiltersWrapper from 'components/common3/PageWithFiltersWrapper/PageWithFiltersWrapper';
import SeoPagination from 'components/common3/SeoPagination/SeoPagination';
// Styles
import styles from './Athletes.module.scss';

const VirtuosoGridItem: React.FC<{
  item: GetStores_stores_entities;
}> = React.memo(({ item }) => {
  return <StoreListItem store={item} type="list" />;
});
VirtuosoGridItem.displayName = 'VirtuosoGridItem';

export const Athletes = () => {
  const { filters, sorting, searchKey, search } = useFiltersContext();
  const router = useRouter();
  const rawPage = getAthletesPageFromQuery(router.query);
  const [queryPage, setQueryPage] = useState(rawPage);

  useEffect(() => {
    setQueryPage(rawPage);
  }, [rawPage]);

  const inputVariables = useMemo(
    () => ({
      orderBy: sorting.order,
      direction: sorting.direction,
      limit: ATHLETES_PER_PAGE,
      offset: (queryPage - 1) * ATHLETES_PER_PAGE,
      storeIds: filters.athleteIds,
      ...(filters?.sports?.length
        ? { sports: filters.sports.map((i) => Number(i)) }
        : {}),
      ...(filters?.hashtagIds?.length
        ? { hashtagIds: filters.hashtagIds.map((i) => Number(i)) }
        : {}),
      ...(filters.isStreamer?.[0] === 'true' ? { isStreamer: true } : {}),
      ...(filters.isShoppingEnabled?.[0] === 'true'
        ? { isShoppingEnabled: true }
        : {}),
      ...(searchKey &&
        search[searchKey] && {
          [searchKey]: search[searchKey],
        }),
    }),
    [filters, sorting, searchKey, search, queryPage]
  );

  const { data, loading, fetchMore } = useQuery<GetStores, GetStoresVariables>(
    GET_STORES,
    {
      variables: {
        storeRoles: [UserRole.Athlete],
        input: {
          ...inputVariables,
        },
        withFilters: true,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const athletes = useMemo(() => data?.stores?.entities || [], [
    data?.stores?.entities,
  ]);
  const total = data?.stores?.total;
  const pageCount =
    total != null ? getAthletesPageCount(total, ATHLETES_PER_PAGE) : undefined;
  const currentPage =
    pageCount != null ? clampAthletesPage(rawPage, pageCount) : rawPage;

  useEffect(() => {
    if (pageCount == null) {
      return;
    }

    const clampedPage = clampAthletesPage(rawPage, pageCount);

    if (queryPage !== clampedPage) {
      setQueryPage(clampedPage);
    }

    if (rawPage === clampedPage) {
      return;
    }

    const query = getAthletesPaginationQuery(router.query);
    if (clampedPage > 1) {
      query.page = String(clampedPage);
    }

    router.replace({ pathname: router.pathname, query }, undefined, {
      shallow: true,
    });
  }, [pageCount, rawPage, queryPage, router]);

  const allFilters = {
    ...data?.stores.filters,
    ...{ isStreamer: filters.isStreamer?.[0] === 'true' ? true : false },
    ...{
      isShoppingEnabled:
        filters.isShoppingEnabled?.[0] === 'true' ? true : false,
    },
  };

  const fetchMoreData = () => {
    const prevOffset =
      data?.stores?.offset ?? (currentPage - 1) * ATHLETES_PER_PAGE;
    fetchMore({
      variables: {
        storeRoles: [UserRole.Athlete],
        input: {
          ...inputVariables,
          offset: prevOffset + ATHLETES_PER_PAGE,
        },
      },
    });
  };

  const hasMore = data ? athletes.length !== total : false;
  const isNotFound = !loading && total === 0;

  const buildPageHref = useCallback(
    (page: number) => buildAthletesPaginatedHref(page, router.query),
    [router.query]
  );

  const structuredData = useMemo(() => {
    const items = athletes
      .filter((athlete) => athlete.slug)
      .map((athlete) => ({
        url: `${getEnvLink()}/${athlete.slug}`,
      }));

    return generateItemListStructuredData('Athletes on MILLIONS', items);
  }, [athletes]);

  const handleOnEndReached = () => {
    if (hasMore) {
      fetchMoreData();
    }
  };

  return (
    <>
      <Head
        image={`${getEnvLink()}/athletes.jpg`}
        title={buildAthletesPageTitle(currentPage)}
        description={ATHLETES_PAGE_DESCRIPTION}
        canonicalLink={buildAthletesCanonicalLink(currentPage)}
        structuredData={structuredData}
      />

      <PageWithFiltersWrapper
        title="Athletes"
        sortKey="Athletes"
        loading={loading}
        filters={allFilters}
        searchBarLocation={SearchLocation.Athletes}
        headerSpaceForMobile={false}
        addSmartBannerPaddingToFilters
      >
        {isNotFound && <NotFound title="Athletes not found" />}

        {!!athletes.length && (
          <VirtuosoGrid
            useWindowScroll
            overscan={ATHLETES_PER_PAGE * 5}
            data={athletes}
            listClassName={styles.root}
            totalCount={data?.stores.total}
            itemContent={(index, item) => <VirtuosoGridItem item={item} />}
            endReached={handleOnEndReached}
            components={{
              Footer: () => {
                if (!hasMore) {
                  return null;
                }

                return <InfiniteScrollLoader />;
              },
            }}
          />
        )}

        <SeoPagination
          basePath={ATHLETES_BASE_PATH}
          currentPage={currentPage}
          totalItems={total}
          perPage={ATHLETES_PER_PAGE}
          buildPageHref={buildPageHref}
        />
      </PageWithFiltersWrapper>
    </>
  );
};

export default Athletes;
